/** Helper based on the Strategy pattern */

export type StrategyShape<RunReturn = any, Arg = any> = {
    /** when canRun returns true, we execute run() */
    canRun: (arg: Arg) => boolean;
    run: (arg: Arg) => RunReturn;
};
type RunStrategyOptions<Arg> = {
    arg?: Arg;
    stopAfterFirstRun?: boolean;
};
type InferRunReturnType<Strategy> =
    Strategy extends StrategyShape<infer RunReturn> ? RunReturn : never;

/** overload with stopAfterFirstRun as false */
export function runStrategy<Strategy extends StrategyShape>(
    strategies: Strategy[],
    options: { stopAfterFirstRun: false; arg?: any },
): InferRunReturnType<Strategy>[];

/** overload with stopAfterFirstRun as true or undefined */
export function runStrategy<Strategy extends StrategyShape, Arg>(
    strategies: Strategy[],
    options?: RunStrategyOptions<Arg>,
): InferRunReturnType<Strategy> | undefined;

/**
 * Helper function based on the Strategy pattern to execute one or more strategies.
 *
 * @template Strategy Extends StrategyShape
 * @template Arg Type of argument passed to strategies
 *
 * @param {Strategy[]} strategies Array of strategy objects
 * @param {RunStrategyOptions<Arg>} [options] Configuration options
 * @param {Arg} [options.arg] Argument to pass to each strategy
 * @param {boolean} [options.stopAfterFirstRun=true] If true, stops after first successful strategy
 *
 * Returns result of first successful strategy if stopAfterFirstRun is true,
 * array of results from all successful strategies if false,
 * or undefined if no strategy can run.
 *
 * @example
 * const fruitStrategies = [
 *   {
 *     canRun: (fruit: string) => fruit === 'apple',
 *     run: () => 'Slice the apple'
 *   },
 *   {
 *     canRun: (fruit: string) => fruit === 'banana',
 *     run: () => 'Peel the banana'
 *   },
 *   {
 *     canRun: (fruit: string) => ['orange', 'lemon'].includes(fruit),
 *     run: (fruit: string) => `Squeeze the ${fruit}`
 *   }
 * ];
 *
 * runStrategy(fruitStrategies, { arg: 'apple' }); // Returns 'Slice the apple'
 * runStrategy(fruitStrategies, { arg: 'orange' }); // Returns 'Squeeze the orange'
 * runStrategy(fruitStrategies, { arg: 'grape' }); // Returns undefined
 * runStrategy(fruitStrategies, { arg: 'lemon', stopAfterFirstRun: false }); // Returns ['Squeeze the lemon']
 */
export function runStrategy<Strategy extends StrategyShape, Arg>(
    strategies: Strategy[],
    options?: RunStrategyOptions<Arg>,
) {
    const stopAfterFirstRun = options?.stopAfterFirstRun ?? true;

    if (stopAfterFirstRun === true) {
        return runOnlyFirstStrategyThatCanRun(strategies, options?.arg);
    }

    return runEveryStrategyThatCanRun(strategies, options?.arg);
}

function runOnlyFirstStrategyThatCanRun<Strategy extends StrategyShape, Arg>(
    strategies: Strategy[],
    arg: Arg,
): InferRunReturnType<Strategy> | undefined {
    const strategy = strategies.find((s) => s.canRun(arg));

    if (!strategy) {
        return;
    }

    // eslint-disable-next-line consistent-return
    return strategy.run(arg) as InferRunReturnType<Strategy>;
}

function runEveryStrategyThatCanRun<Strategy extends StrategyShape, Arg>(
    strategies: Strategy[],
    arg: Arg,
): InferRunReturnType<Strategy>[] {
    return strategies.filter((s) => s.canRun(arg)).map((s) => s.run(arg));
}
